import { signal } from '@preact/signals-react';
import {
  cloneDeep,
  compact,
  concat,
  every,
  filter,
  find,
  first,
  flatMap,
  forEach,
  has,
  head,
  includes,
  isEmpty,
  isNumber,
  join,
  keys,
  map,
  partition,
  pick,
  replace,
  size,
  slice,
  sortBy,
  split,
  startsWith,
  uniq,
  uniqBy,
  values,
} from 'es-toolkit/compat';

import { ShapeType } from 'components/Procedure/Header/SlideInteractionMenu/SlideAnnotationTools/options';
import { GeoJsonFeature } from 'components/Procedure/SlidesViewer/DeckGLViewer/layers/NebulaGLExtensions/geojson-types';
import { Annotation, TodoDefinition, TodoOption } from 'interfaces/annotation';
import { CellRuleValue } from 'interfaces/cellRule';
import { MULTIPLEX_STAIN_ID } from 'interfaces/stainType';
import {
  getSettingsKeyFromLayerIdComponents,
  LayerVisualizationSettings,
  slidesLayerVisualizationSettings,
} from '../../components/Procedure/Infobar/slidesVisualizationAndConfiguration';

// We only allow annotating the left viewer slide
export const ANNOTATING_VIEWER_INDEX = 0;

export const ANNOTATION_LAYER_KEY_SUFFIX = '__annotationLayer__';

export const isAnnotationsLayerKey = (key: string) => {
  return startsWith(key, ANNOTATION_LAYER_KEY_SUFFIX);
};

export const getAnnotationLayerDisplayNameFromKey = (key: string) => key.replace(ANNOTATION_LAYER_KEY_SUFFIX, '');

export const getAnnotationLabel = (annotation: Annotation) =>
  `${annotation?.annotationAssignment?.annotationAssignmentId} - ${annotation?.annotationAssignment?.name} (${annotation?.workingState})`;

export const getAnnotationLayerSettingsKey = (annotation: Annotation) =>
  `${ANNOTATION_LAYER_KEY_SUFFIX}${getAnnotationLabel(annotation)}`;

const SORT_PADDING_LENGTH = 100000;
const MARKER_PARTS_REGEX = /([a-zA-Z]+)|(\d+)/g;
const DIGITS_REGEX = /^\d+$/;

// This custom sorting logic for marker keys is designed to replicate PostgreSQL's natural sort order.
// This ensures that the order of markers is consistent between the ui and the database.
// For example, it will sort [ 'cd4', 'cd68', 'gfap', 'cd163', 'cd3', 'cd45', 'cd34', ] into ['cd163', 'cd34', 'cd3', 'cd45', 'cd4', 'cd68', 'gfap'].
export const sortMarkerKeys = (markerKeys: string[]) => {
  return sortBy(markerKeys, (markerPositivityKey) => {
    const parts = markerPositivityKey.match(MARKER_PARTS_REGEX) || [];
    return map(parts, (part) => {
      if (DIGITS_REGEX.test(part)) {
        // Sort key for numeric parts.
        // Example: part = '163' -> key = `1-1-${String(SORT_PADDING_LENGTH - 3)}-163`
        // Example: part = '34'  -> key = `1-3-${String(SORT_PADDING_LENGTH - 2)}-34`
        // Example: part = '3'   -> key = `1-3-${String(SORT_PADDING_LENGTH - 1)}-3`
        // Explanation:
        // - Leading '1' ensures numbers come after letters when compared as tuples.
        // - First digit groups by leading digit to reproduce custom order nuances.
        // - Length inversion (SORT_PADDING_LENGTH - size(part)) makes longer numbers sort before shorter ones.
        // - The raw numeric string is used as a final tie-breaker.
        return `1-${head(part)}-${String(SORT_PADDING_LENGTH - size(part))}-${part}`;
      }
      // Sort key for alphabetic parts.
      // Example: part = 'cd' -> key = `0-cd`
      // Leading '0' ensures alphabetic segments are prioritized before numeric segments.
      return `0-${part}`;
    });
  });
};

export const getMarkersKey = (markerPositivity: { [key: string]: number | undefined }) => {
  if (isEmpty(markerPositivity)) {
    return '';
  }

  const sortedMarkerKeys = sortMarkerKeys(keys(markerPositivity));
  const markersPositivityStrings = map(sortedMarkerKeys, (markerId) => {
    const value = markerPositivity?.[markerId];
    const suffix = value === CellRuleValue.MustBePositive ? CLASS_ID_MARKER_POS_SUFFIX : CLASS_ID_MARKER_NEG_SUFFIX;
    return `${markerId}${CLASS_ID_MARKER_VALUE_SEPARATOR}${suffix}`;
  });
  const markersKey = join(markersPositivityStrings, CLASS_ID_SEPARATOR);

  return markersKey;
};

export const getTodoClassKey = (todoOption: TodoOption) => {
  const markersKey = getMarkersKey(todoOption?.markerPositivity || {});

  if (isEmpty(markersKey)) {
    return todoOption?.name;
  }

  return `${todoOption?.name}${CLASS_ID_SEPARATOR}${markersKey}`;
};

export const getMarkersDifferencesText = (option: TodoOption, todoOptions: TodoOption[]) => {
  const anotherOptionsWithSameName = filter(todoOptions, (otherOption) => {
    return otherOption.name === option.name && getTodoClassKey(otherOption) !== getTodoClassKey(option);
  });

  const differenceInMarkers = uniq(
    flatMap(anotherOptionsWithSameName, (anotherOption) => {
      // get all the marker positivity differences - if the key exists in one but not the other, consider it a difference, if the key exists in both but with different values, consider it a difference
      return filter(keys(option.markerPositivity), (markerId) => {
        return (
          !has(anotherOption.markerPositivity, markerId) ||
          anotherOption.markerPositivity?.[markerId] !== option.markerPositivity?.[markerId]
        );
      });
    })
  );

  const differenceInMarkersText = replace(
    getMarkersKey(pick(option.markerPositivity, differenceInMarkers)),
    CLASS_ID_SEPARATOR_REGEX,
    ', '
  );

  return differenceInMarkersText;
};

export const getAnnotationOptionSettingsKey = (annotation: Annotation, todoOption: TodoOption) =>
  getSettingsKeyFromLayerIdComponents({
    parentKey: getAnnotationLayerSettingsKey(annotation),
    layerName: getTodoClassKey(todoOption),
  });

export const getAnnotationTodoNameAndDefinition = (
  annotation?: Annotation
): {
  name?: string;
  todo?: TodoDefinition;
} => {
  const annotationAssignmentTodos = values(annotation?.annotationAssignment?.todos);
  const todoNames = map(annotation?.annotationsData, 'todo');

  if (size(annotationAssignmentTodos) > 1) {
    console.warn("Found multiple todos for annotation, shouldn't happen", {
      annotation,
      todoNames,
      annotationAssignmentTodos,
    });
  }
  const todoName = !isEmpty(todoNames) ? first(todoNames) : 'Unnamed';
  return { name: todoName, todo: first(annotationAssignmentTodos) };
};

export const MULTIPLEX_UNKNOWN_DIAGNOSIS_DEFAULT_COLOR = '#FFFFFF'; // white
export const NON_MULTIPLEX_UNKNOWN_DIAGNOSIS_DEFAULT_COLOR_HEX = '#000000'; // black

export const getAssignmentTodosWithLegacy = ({
  annotation,
  slideStainTypeId,
  getTodoOptionFromClassId,
}: {
  annotation: Annotation;
  slideStainTypeId: string;
  getTodoOptionFromClassId: (classId: string | undefined | null, fallbackColor?: string) => TodoOption;
}): TodoOption[] => {
  const { todo: currentTodo } = getAnnotationTodoNameAndDefinition(annotation);
  const todoOptions = currentTodo?.options || [];
  const todoOptionClassKeys = map(todoOptions, getTodoClassKey);

  const annotationMarkers = flatMap(concat(annotation?.annotationsData, annotation?.draftAnnotationsData), 'markers');

  // annotations that exist in the annotations but not in the current todo options
  const legacyAnnotationClassKeys = uniqBy(
    filter(
      map(annotationMarkers, (marker) => {
        const diagnosis = marker?.properties?.diagnosis;
        if (!diagnosis) {
          return null;
        }

        return getTodoClassKey({
          name: diagnosis,
          markerPositivity: marker?.properties?.markerPositivity,
        });
      }),
      (currentClassKey) => Boolean(currentClassKey) && !includes(todoOptionClassKeys, currentClassKey)
    ),
    (currentClassKey) => currentClassKey
  );

  const fallbackColor =
    slideStainTypeId === MULTIPLEX_STAIN_ID
      ? MULTIPLEX_UNKNOWN_DIAGNOSIS_DEFAULT_COLOR
      : NON_MULTIPLEX_UNKNOWN_DIAGNOSIS_DEFAULT_COLOR_HEX;

  const legacyMockTodos = map(legacyAnnotationClassKeys, (currentClassKey) => ({
    ...getTodoOptionFromClassId(currentClassKey, fallbackColor),
    deletedAt: new Date(),
  }));

  return concat(todoOptions, legacyMockTodos);
};

export const defaultAnnotationVisualSettings: LayerVisualizationSettings = {
  show: false,
  selected: false,
  opacity: 100,
  color: '#dddddd',
};

export const showNewAnnotationAssignmentIdForSlide = ({
  activeAnnotationAssignmentId,
  slideAnnotations,
  viewerIndex,
  slideId,
}: {
  activeAnnotationAssignmentId: number;
  slideAnnotations: Array<Annotation>;
  viewerIndex: number;
  slideId: string;
}) => {
  if (!isNumber(activeAnnotationAssignmentId)) {
    return;
  }
  const viewerSlideLayerVisualizationSettings = slidesLayerVisualizationSettings[viewerIndex];
  if (!viewerSlideLayerVisualizationSettings) {
    console.error('No viewerSlideLayerVisualizationSettings found for viewerIndex', viewerIndex);
    return;
  }
  const newSettings = viewerSlideLayerVisualizationSettings.value;
  if (!newSettings?.[slideId]) {
    newSettings[slideId] = {};
  }
  const slideLayerVisualizationSettings = newSettings[slideId];

  const savedAnnotationData = find(slideAnnotations, {
    annotationAssignment: { annotationAssignmentId: activeAnnotationAssignmentId },
  });
  if (!savedAnnotationData) {
    console.error('No savedAnnotationData found for activeAnnotationAssignmentId', activeAnnotationAssignmentId);
    return;
  }
  const parentKey = getAnnotationLayerSettingsKey(savedAnnotationData);
  const parentLabel = getAnnotationLabel(savedAnnotationData);
  const parentSignal = slideLayerVisualizationSettings?.[parentKey];
  if (!parentSignal) {
    const defaultSettings = {
      ...defaultAnnotationVisualSettings,
      show: true,
      selected: true,
      displayName: parentLabel,
    };
    slideLayerVisualizationSettings[parentKey] = signal(defaultSettings);
  } else {
    const previousSettings = parentSignal.value;
    parentSignal.value = { ...previousSettings, selected: true, show: true };
  }

  const { todo } = getAnnotationTodoNameAndDefinition(savedAnnotationData);

  forEach(todo?.options, (option) => {
    const annotationSettingsKey = getAnnotationOptionSettingsKey(savedAnnotationData, option);
    const layerSignal = slideLayerVisualizationSettings?.[annotationSettingsKey];
    if (!layerSignal) {
      slideLayerVisualizationSettings[annotationSettingsKey] = signal({
        ...defaultAnnotationVisualSettings,
        show: true,
        selected: true,
        ...(option.color ? { color: option.color } : {}),
      });
    } else {
      const previousSettings = layerSignal.value;
      layerSignal.value = { ...previousSettings, selected: true, show: true };
    }
  });

  viewerSlideLayerVisualizationSettings.value = newSettings;
};

// When annotating taxonomy classes in multiplex with multiple cell rules values options,
// we want to distinguish between different types of annotations (whether the cell is positive or negative for a specific stain type).
// for example, we can annotate the same cell type according to 2 stains:
// - cell type A, stain type 1 positive and stain type 2 positive
// - cell type A, stain type 1 positive and stain type 2 negative
// we build the final class name by adding the stain type and the positivity to the class name.
// taxonomyId__marker1-pos__marker2-neg__marker3-pos
// the display name will look like this: (Cell Type A is the display name of the cell type)
// Cell Type A (marker1-pos, marker2-neg, marker3-pos)

export const CLASS_ID_SEPARATOR = '__';
export const CLASS_ID_MARKER_VALUE_SEPARATOR = '-';
export const CLASS_ID_MARKER_POS_SUFFIX = 'pos';
export const CLASS_ID_MARKER_NEG_SUFFIX = 'neg';

const CLASS_ID_SEPARATOR_REGEX = new RegExp(CLASS_ID_SEPARATOR, 'g');

export const convertOptionsWithRule2ToMultipleOptions = (options: TodoOption[]): TodoOption[] => {
  if (
    every(options, (option) =>
      every(values(option?.markerPositivity), (value) => value !== CellRuleValue.OneOfTwoMustBePositive)
    )
  ) {
    return options;
  }

  const newOptions: TodoOption[] = [];
  forEach(options, (option) => {
    if (every(values(option?.markerPositivity), (value) => value !== CellRuleValue.OneOfTwoMustBePositive)) {
      newOptions.push(option);
      return;
    }

    const [markersWithRule2] = partition(keys(option.markerPositivity), (markerId) => {
      return option.markerPositivity?.[markerId] === CellRuleValue.OneOfTwoMustBePositive;
    });

    let currentCellTypeFlatOptions: TodoOption[] = [option];

    forEach(markersWithRule2, (markerIdWithRule2) => {
      const flatOptionsWithCurrentMarker: TodoOption[] = [];
      forEach(currentCellTypeFlatOptions, (currentOption) => {
        flatOptionsWithCurrentMarker.push({
          ...currentOption,
          markerPositivity: {
            ...currentOption.markerPositivity,
            [markerIdWithRule2]: CellRuleValue.MustBePositive,
          },
        });
        flatOptionsWithCurrentMarker.push({
          ...currentOption,
          markerPositivity: {
            ...currentOption.markerPositivity,
            [markerIdWithRule2]: CellRuleValue.MustBeNegative,
          },
        });
      });
      currentCellTypeFlatOptions = cloneDeep(flatOptionsWithCurrentMarker);
    });

    newOptions.push(...currentCellTypeFlatOptions);
  });

  return newOptions;
};

export const getTaxonomyIdAndMarkersFromClassId = (
  classId: string
): {
  taxonomyId?: string;
  markers: string[];
} => {
  const parts = split(classId, CLASS_ID_SEPARATOR);
  const taxonomyId = first(parts);
  const markers = slice(parts, 1);
  return { taxonomyId, markers };
};

export const getAnnotationsClassDisplayName = (
  id: string,
  taxonomyIdToDisplayNameMap: Record<string, string>,
  stainTypeIdToDisplayName: (stain: string) => string,
  labelsKeys: string[]
) => {
  const { taxonomyId, markers } = getTaxonomyIdAndMarkersFromClassId(id);

  const anotherLabelsKeysWithSameTaxonomy = filter(labelsKeys, (labelKey) => {
    const { taxonomyId: otherTaxonomyId } = getTaxonomyIdAndMarkersFromClassId(labelKey);
    return otherTaxonomyId === taxonomyId && labelKey !== id;
  });

  const differenceInMarkers = compact(
    uniq(
      flatMap(anotherLabelsKeysWithSameTaxonomy, (anotherLabelKey) => {
        const { markers: otherMarkers } = getTaxonomyIdAndMarkersFromClassId(anotherLabelKey);
        return filter(markers, (markerId) => {
          return !includes(otherMarkers, markerId);
        });
      })
    )
  );

  const taxonomyDisplayName = taxonomyId ? taxonomyIdToDisplayNameMap[taxonomyId] || taxonomyId : taxonomyId;
  const markersDisplayName = !isEmpty(differenceInMarkers)
    ? ` (${join(
        map(differenceInMarkers, (marker) => stainTypeIdToDisplayName(marker) || marker || 'Unknown Marker'),
        ', '
      )})`
    : '';
  return `${taxonomyDisplayName}${markersDisplayName}`;
};

export const getShapeTypeFromTodoClassName = (className?: string | null): ShapeType | undefined => {
  const normalizedClassName = className?.toLocaleLowerCase?.();

  if (normalizedClassName === 'point') {
    return ShapeType.Point;
  }

  if (includes(['roi', 'rect', 'rectangle'], normalizedClassName)) {
    return ShapeType.Rect;
  }

  if (normalizedClassName === 'polygon') {
    return ShapeType.Polygon;
  }

  return undefined;
};

export const isRoiClassLabel = (value?: string | null): boolean => {
  const normalized = value?.toLocaleLowerCase?.().trim();
  if (!normalized) return false;
  return normalized.split(/[^a-z0-9]+/).includes('roi');
};

// Assignment ROI classes are stored with className "polygon" and a name such as "roi" or "ROI".
// The rectangle tool is what creates those classes; a free polygon tool is not a substitute.
export const getShapeTypeFromTodoOption = (todoOption: TodoOption): ShapeType => {
  if (todoOption?.shapeType) return todoOption.shapeType;
  const classNameShape = getShapeTypeFromTodoClassName(todoOption?.className);
  if (classNameShape === ShapeType.Point || classNameShape === ShapeType.Rect) return classNameShape;
  if (isRoiClassLabel(todoOption?.name) || isRoiClassLabel(todoOption?.displayName)) return ShapeType.Rect;
  return classNameShape || ShapeType.Polygon;
};

export const isTodoOptionCompatibleWithFeature = (
  feature: Pick<GeoJsonFeature, 'geometry'> | undefined,
  todoOption: TodoOption
) => {
  const featureGeometryType = feature?.geometry?.type;
  const optionShapeType = getShapeTypeFromTodoOption(todoOption);

  if (featureGeometryType === 'Point') {
    return optionShapeType === ShapeType.Point;
  }

  if (featureGeometryType === 'Polygon') {
    return includes([ShapeType.Rect, ShapeType.Polygon], optionShapeType);
  }

  return false;
};
